import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { AccountFinal } from "@/api/entities";
import { PersonFinal } from "@/api/entities";
import { MomentumScore } from "@/api/entities";
import { Procurement } from "@/api/entities";
import { Note } from "@/api/entities";
import { Deal } from "@/api/entities";
import { ProcurementProfileData, IspProfileData, ContentProfileData } from "@/api/entities";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Building2, AlertCircle, RefreshCw, Info, ChevronLeft } from "lucide-react";
import { sanitizeId } from "@/components/utils/id";
import { applyTenantOverrides } from "@/components/utils/tenantOverrides";
import { resolveAccount } from "@/components/utils/accountResolver";
import { isNotInPilotError } from "@/lib/pilotMode";
import AccountPilotLocked from "../components/accounts/AccountPilotLocked";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useTranslation, translate } from "@/components/utils/translations";
import { Department } from "@/api/entities";

import AccountHeader from "../components/accounts/AccountHeader";
import CalmedicaOrganisationLinks from "../components/accounts/CalmedicaOrganisationLinks";
import DecisionWindow from "../components/accounts/DecisionWindow";
import MomentumSummary from "../components/accounts/MomentumSummary";
import TechStack from "../components/accounts/TechStack";
import MarketAccountTechStack from "../components/accounts/MarketAccountTechStack";
import StrategyCards from "../components/accounts/StrategyCards";
import PersonasList from "../components/accounts/PersonasList";
import AccountSignalList from "../components/accounts/AccountSignalList";
import Notes from "../components/accounts/Notes";
import LiveDealsCard from "../components/accounts/LiveDealsCard";
import ProcurementProfile from "../components/accounts/ProcurementProfile";
import ContentProfile from "../components/accounts/ContentProfile";
import IspProfile from "../components/accounts/IspProfile";
import ArchetypeGrid from "../components/accounts/ArchetypeGrid";
import FinancialsTab from "../components/accounts/FinancialsTab";
import SellingRationaleSection from "../components/accounts/SellingRationaleSection";
import { hasAccountFinancialsTab } from "@/lib/accountFinancials";
import MobileTooltip from "../components/MobileTooltip";
import DepartmentsList from "../components/accounts/DepartmentsList";
import PlaybooksTab from "../components/accounts/PlaybooksTab";
import SalesMapTab from "../components/accounts/SalesMapTab";
import AccountCompetitorsTabContent from "../components/competitors/AccountCompetitorsTabContent";
import { fetchAccountCompetitiveProximity } from "@/lib/competitorsApi";
import { prefetchAccountSalesMap } from "@/lib/salesMapApi";
import { createPageUrl } from "@/utils";
import { useUser, useCurrentTenant } from '@/contexts/UserContext';
import { useChatSession } from '@/contexts/ChatSessionContext';
import { resolveEffectiveTenant, isLibraryAccountPageTenant, isBibliothecaFamilyTenant } from "@/lib/tenantUtils";
import { isSysExamProductContextTenant } from "@/lib/accountTargetProducts";
import { notify } from "@/components/ui/notify";
import {
  requestAccountEnrichment,
  resolveMarketModuleHint,
  getCachedMarketModuleHint,
  removeMarketAccount,
  clearAccountPageCache,
  MarketModuleOffError,
} from "@/lib/marketExperienceApi";
import { interpretRemoveAccountResult, isRemovedFromListAccount, ENRICH_REQUEST_TOAST } from "@/lib/marketExperience";
import { useAccountRenewalRisk } from "@/hooks/useRenewalRisk";
import RemoveAccountDialog from "../components/accounts/RemoveAccountDialog";

const sanitizeMomentumChange = (value) => {
  if (value == null || value === '') return null;
  if (typeof value === 'number') return Number.isNaN(value) ? null : value;
  if (typeof value === 'string') {
    const cleaned = value.replace(/–/g, '-').trim();
    if (!cleaned) return null;
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? null : parsed;
  }
  return null;
};

const getProcurementColor = (status) => {
  switch (status) {
    case 'Friendly': return 'bg-green-50 border-green-200 text-green-800';
    case 'Neutral': return 'bg-yellow-50 border-yellow-200 text-yellow-800';
    case 'Challenging': return 'bg-red-50 border-red-200 text-red-800';
    default: return 'bg-gray-50 border-gray-200 text-gray-800';
  }
};

// Normalize departments with stable IDs and numeric scores - preserve all fields including rationale
function normalizeDepartments(raw, accountId) {
  return (raw || []).map((row, i) => ({
    ...row, // Preserve all fields including department_fit_rationale
    id: row.id || `${accountId}-${(row.department || 'unknown').toLowerCase().replace(/\s+/g,'-')}-${i}`,
    department_fit_score: row.department_fit_score !== undefined && row.department_fit_score !== null 
      ? Number(row.department_fit_score) 
      : 0,
    // Explicitly preserve department_fit_rationale to ensure it's not lost
    department_fit_rationale: row.department_fit_rationale !== undefined ? row.department_fit_rationale : null,
  }));
}

const ACCOUNT_CACHE_KEY_PREFIX = 'account-final-v3-';
const CACHE_DURATION = 30 * 60 * 1000;

function bounceRemovedFromList(account, navigate) {
  if (!isRemovedFromListAccount(account)) return false;
  notify({
    title: 'This account is not in your list',
    description: 'You can add it back any time from Market — its pursuit status and notes are kept.',
  });
  navigate(createPageUrl('Market'), { replace: true });
  return true;
}

const isValidAccountId = (id) => {
  if (!id) return false;
  if (/^[a-f0-9]{24}$/i.test(id)) return true;
  if (typeof id === 'string' && id.includes('_')) return true;
  if (/^\d+$/.test(String(id))) return true;
  return false;
};
const RATE_LIMIT_COOLDOWN_KEY = 'account-rate-limit-cooldown';
const COOLDOWN_DURATION = 5 * 60 * 1000;

function readAccountCache(accountId, tenantId) {
  try {
    // Clear old cache format (without tenant_id in key) on first read
    const oldKey = `${ACCOUNT_CACHE_KEY_PREFIX}${accountId}`;
    if (sessionStorage.getItem(oldKey)) {
      console.log('🧹 AccountFinal: Clearing old cache format (without tenant_id) for:', accountId);
      sessionStorage.removeItem(oldKey);
    }
    
    const key = `${ACCOUNT_CACHE_KEY_PREFIX}${tenantId}-${accountId}`;
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const data = JSON.parse(raw);
    
    // Reject cache data without tenantId field (old format)
    if (!data.tenantId) {
      console.warn('⚠️ AccountFinal: Cache missing tenantId field, clearing old cache for:', accountId);
      sessionStorage.removeItem(key);
      return null;
    }
    
    // Verify tenant matches (safety check)
    if (data.tenantId !== tenantId) {
      console.warn('⚠️ AccountFinal: Cache tenant mismatch, clearing:', { cached: data.tenantId, current: tenantId, accountId });
      sessionStorage.removeItem(key);
      return null;
    }
    return {
      account: data.account,
      personas: data.personas || [],
      signals: data.signals || [],
      momentumScores: data.momentumScores || [],
      procurements: data.procurements || [],
      notes: data.notes || [],
      deals: data.deals || [],
      procurementProfile: data.procurementProfile || null,
      ispProfile: data.ispProfile || null,
      contentProfile: data.contentProfile || null,
      departments: data.departments || [],
      cachedAt: data.cachedAt,
      isStale: Date.now() - data.cachedAt > CACHE_DURATION
    };
  } catch { return null; }
}

function writeAccountCache(accountId, tenantId, data) {
  try {
    const key = `${ACCOUNT_CACHE_KEY_PREFIX}${tenantId}-${accountId}`;
    sessionStorage.setItem(
      key,
      JSON.stringify({ ...data, tenantId, cachedAt: Date.now() })
    );
  } catch {}
}

function isInRateLimitCooldown() {
  try {
    const cooldownEnd = localStorage.getItem(RATE_LIMIT_COOLDOWN_KEY);
    if (!cooldownEnd) return false;
    const remaining = Date.now() - parseInt(cooldownEnd);
    if (remaining >= 0) {
      localStorage.removeItem(RATE_LIMIT_COOLDOWN_KEY);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

function setRateLimitCooldown() {
  try {
    localStorage.setItem(RATE_LIMIT_COOLDOWN_KEY, String(Date.now() + COOLDOWN_DURATION));
    console.log('🚫 Rate limit cooldown set for 5 minutes');
  } catch {}
}

function clearRateLimitCooldown() {
  try {
    localStorage.removeItem(RATE_LIMIT_COOLDOWN_KEY);
    console.log('✅ Rate limit cooldown cleared');
  } catch {}
}

const normalizeAccountName = (name) => name ? String(name).toLowerCase().trim() : '';

export default function AccountFinalPage({ startChatSession: startChatSessionProp, currentTenant: currentTenantProp }) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const tabFromUrl = useMemo(
    () => new URLSearchParams(location.search).get('tab'),
    [location.search],
  );
  const contextUser = useUser();
  const contextTenant = useCurrentTenant();
  const chatSession = useChatSession();
  const startChatSession = startChatSessionProp ?? chatSession?.startChatSession;
  const currentTenant = currentTenantProp || contextTenant || chatSession?.currentTenant;
  const registerPageAccount = chatSession?.registerPageAccount;
  const [account, setAccount] = useState(null);
  const [personas, setPersonas] = useState([]);
  const [signals, setSignals] = useState([]);
  const [momentumScores, setMomentumScores] = useState([]);
  const [procurements, setProcurements] = useState([]);
  const [notes, setNotes] = useState([]);
  const [deals, setDeals] = useState([]);
  const [procurementProfile, setProcurementProfile] = useState(null);
  const [ispProfile, setIspProfile] = useState(null);
  const [contentProfile, setContentProfile] = useState(undefined);
  const [departments, setDepartments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [rateLimitError, setRateLimitError] = useState(false);
  const [usingStaleCache, setUsingStaleCache] = useState(false);
  const [playbookCount, setPlaybookCount] = useState(0);
  const [notesCount, setNotesCount] = useState(0);
  const [competitorProximityRow, setCompetitorProximityRow] = useState(undefined);
  const [marketTechUnavailable, setMarketTechUnavailable] = useState(false);
  const [marketBadges, setMarketBadges] = useState(null);
  const [marketModuleOn, setMarketModuleOn] = useState(null); // null = unknown, show optimistically only with cache
  const [marketLens, setMarketLens] = useState(null);
  const [enrichBusy, setEnrichBusy] = useState(false);
  const [enrichQueuedAt, setEnrichQueuedAt] = useState(null);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [removeBusy, setRemoveBusy] = useState(false);
  const [pilotLocked, setPilotLocked] = useState(null);

  // Tab label uses notesCount; Radix does not mount inactive TabsContent, so <Notes> never runs
  // onNotesCountChange until the user opens the Notes tab — keep the badge in sync with bulk-loaded notes.
  useEffect(() => {
    setNotesCount(Array.isArray(notes) ? notes.length : 0);
  }, [notes]);

  useEffect(() => {
    if (!registerPageAccount) return undefined;
    registerPageAccount(account);
    return () => registerPageAccount(null);
  }, [account, registerPageAccount]);

  useEffect(() => {
    setMarketTechUnavailable(false);
    setMarketBadges(null);
    setEnrichQueuedAt(null);
    // Keep marketModuleOn / marketLens across accounts in the same tenant so the
    // empty-personas enrich CTA does not wait on another probe.
  }, [account?.account_id, account?.id, account?.tenant_id]);

  // Fetch playbook count when account loads
  useEffect(() => {
    let mounted = true;
    const fetchPlaybookCount = async () => {
      if (!account?.account_id && !account?.id) return;
      
      try {
        const accountId = account.account_id || account.id;
        const tenantId = resolveEffectiveTenant(contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null, currentTenant, contextUser, 'instructure');
        
        const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
          ? 'https://api.neogtm.com' 
          : 'https://api.neogtm.com';
        
        const response = await fetch(
          `${API_BASE_URL}/api/playbooks/accounts/${accountId}/summary?tenant_id=${tenantId}&user_id=${encodeURIComponent(contextUser?.id || '')}`,
          { credentials: 'include' }
        );
        
        if (response.ok && mounted) {
          const data = await response.json();
          const count = data.active_playbooks?.length || 0;
          setPlaybookCount(count);
        }
      } catch (error) {
        if (mounted) {
          console.error('Error fetching playbook count:', error);
          setPlaybookCount(0);
        }
      }
    };
    
    if (account) {
      fetchPlaybookCount();
    }
    
    return () => { mounted = false; };
  }, [account, currentTenant]);

  const loadAccountData = useCallback(async (identifier, forceRefresh = false) => {
    
    if (!identifier) {
      console.error('No account ID provided');
      setIsLoading(false);
      return;
    }

    setPilotLocked(null);
    const fromNav = location.state?.pilotLocked;
    if (fromNav) {
      setPilotLocked(fromNav);
      setIsLoading(false);
    }

    // Try to read cache by identifier (could be UUID or account_id)
    // After resolving, we'll use account_id for cache key
    const tenantId = resolveEffectiveTenant(contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null, currentTenant, contextUser, 'instructure');
    const cached = fromNav ? null : readAccountCache(identifier, tenantId);
    
    if (cached && isInRateLimitCooldown() && !forceRefresh) {
      console.log('⏸️ AccountFinal: In cooldown, using cached data');
      if (bounceRemovedFromList(cached.account, navigate)) return;
      setAccount(cached.account);
      setPersonas(cached.personas || []);
      setSignals(cached.signals || []);
      setMomentumScores(cached.momentumScores || []);
      setProcurements(cached.procurements || []);
      setDeals(cached.deals || []);
      setProcurementProfile(cached.procurementProfile || null);
      setIspProfile(cached.ispProfile || null);
      setContentProfile(cached.contentProfile || null);
      setNotes(cached.notes || []);
      setDepartments(cached.departments || []);
      setRateLimitError(true);
      setUsingStaleCache(cached.isStale);
      setIsLoading(false);
      return;
    }

    if (cached && !cached.isStale && !forceRefresh) {
      console.log('📦 AccountFinal: Using fresh cache');
      if (bounceRemovedFromList(cached.account, navigate)) return;
      setAccount(cached.account);
      setPersonas(cached.personas || []);
      setSignals(cached.signals || []);
      setMomentumScores(cached.momentumScores || []);
      setProcurements(cached.procurements || []);
      setDeals(cached.deals || []);
      setProcurementProfile(cached.procurementProfile || null);
      setIspProfile(cached.ispProfile || null);
      setContentProfile(cached.contentProfile || null);
      setNotes(cached.notes || []);
      setDepartments(cached.departments || []);
      setRateLimitError(false);
      setUsingStaleCache(false);
      setIsLoading(false);
      return;
    }

    if (cached && cached.isStale && !forceRefresh) {
      console.log('📦 AccountFinal: Using stale cache, will try background refresh');
      if (bounceRemovedFromList(cached.account, navigate)) return;
      setAccount(cached.account);
      setPersonas(cached.personas || []);
      setSignals(cached.signals || []);
      setMomentumScores(cached.momentumScores || []);
      setProcurements(cached.procurements || []);
      setDeals(cached.deals || []);
      setProcurementProfile(cached.procurementProfile || null);
      setIspProfile(cached.ispProfile || null);
      setContentProfile(cached.contentProfile || null);
      setNotes(cached.notes || []);
      setDepartments(cached.departments || []);
      setUsingStaleCache(true);
      setIsLoading(false);
    }

    try {
      if (!cached) {
        setIsLoading(true);
      }
      
      const tenantId = resolveEffectiveTenant(contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null, currentTenant, contextUser, 'instructure');
      const isCalmedicaLoad = tenantId === 'calmedica';

      console.log('🔄 AccountFinal: Fetching fresh data for tenant:', tenantId);

      const accountData = await resolveAccount(identifier, tenantId);
      setPilotLocked(null);
      
      if (!accountData) {
        console.warn(`Account with identifier '${identifier}' not found.`);
        if (!cached) {
          setAccount(null);
          setIsLoading(false);
        }
        return;
      }

      // Ensure we have the text account_id (not UUID) for all subsequent queries
      if (!accountData.account_id) {
        console.error('⚠️ Account resolved but missing account_id field:', accountData);
        if (!cached) {
          setAccount(null);
          setIsLoading(false);
        }
        return;
      }

      const previewAccount = applyTenantOverrides(accountData, tenantId);
      if (bounceRemovedFromList(previewAccount, navigate)) return;

      console.log('🏥 Account data loaded:', {
        identifier: identifier,
        account_id: accountData.account_id,
        tenant_id: accountData.tenant_id,
        account_name: accountData.account_name
      });

      // Build tenant filter for related data (same logic as Market.jsx)
      let tenantFilter;
      if (tenantId === 'instructure') {
        tenantFilter = { $in: ['instructure', 'clairelabs', 'default', 'global'] };
      } else if (tenantId === 'smowl') {
        tenantFilter = { $in: ['smowl'] };
      } else if (tenantId === 'diambra') {
        tenantFilter = { $in: ['diambra', 'global'] };
      } else if (tenantId === 'clairelabs') {
        tenantFilter = { $in: ['clairelabs', 'default', 'global'] };
      } else if (tenantId === 'faria' || tenantId === 'edqwest') {
        tenantFilter = { $in: ['faria', 'edqwest', 'default', 'global'] };
      } else if (tenantId === 'calmedica') {
        tenantFilter = { $in: [tenantId] };
      } else if (tenantId === 'sebit') {
        tenantFilter = { $in: [tenantId, 'global'] };
      } else if (tenantId === 'plataformaa') {
        tenantFilter = { $in: [tenantId, 'global'] };
      } else {
        tenantFilter = { $in: [tenantId, 'default', 'global'] };
      }

      // Learnwise: procurement_profile_data from tenant_id=global should appear; match by account_id
      const profileTenantFilter = tenantId === 'learnwise'
        ? { $in: ['learnwise', 'global'] }
        : tenantFilter;

      // [2026-10-20] TENANT-CLONED ACCOUNT PAGES MISSED EVERY DEAL.
      // A cloned account resolves account_id to `<tenantId>_<base>` (niryo_UKFE-086),
      // but `deals` stores the BASE id — measured: of 1,000 sampled non-null
      // account_ids in `deals` (and 1,000 in `closed_deals`) every one is a base id
      // and none carries a tenant prefix. So the Live Deals fetch below asked for
      // `niryo_UKFE-086`, got nothing, and Hopwood Hall College's humanoid-robot
      // tender (status Live, account_id UKFE-086) never reached the card. 38 UK
      // accounts have both a base id and a clone, so this was never Hopwood-specific.
      // Strip ONLY an exact `<tenantId>_` prefix — never a guessed one — and ask for
      // both ids, keeping the clone id first so a genuinely tenant-scoped row still wins.
      const baseAccountId =
        tenantId && typeof accountData.account_id === 'string' &&
        accountData.account_id.startsWith(`${tenantId}_`)
          ? accountData.account_id.slice(tenantId.length + 1)
          : null;
      const dealAccountIdFilter = baseAccountId
        ? { $in: [accountData.account_id, baseAccountId] }
        : accountData.account_id;
      const matchesDealAccount = (record) =>
        record?.account_id === accountData.account_id ||
        (!!baseAccountId && record?.account_id === baseAccountId);

      // [2026-10-20] PERSONAS NEVER REACHED A TENANT-CLONED ACCOUNT PAGE — TWO FAULTS, EITHER FATAL.
      // (1) `personas` base rows live at tenant_id `global_temp` (the shared institutional layer;
      //     the per-tenant judgement sits in tenant_overrides), but `tenantFilter` resolves to
      //     ['<tenant>','default','global'] and so excluded every one of them at the BACKEND.
      // (2) the fetch asked for the clone id `<tenantId>_<base>` while personas store the BASE id.
      // Measured on dreamapply: Universidade de Aveiro returned 0 personas under the filter as it
      // stood and 12 with global_temp added, and 0 for `dreamapply_pt_he_universidade_de_aveiro`
      // against 12 for `pt_he_universidade_de_aveiro`. The name fallback did not save it because it
      // pinned the same tenant_id. Note the file already makes exactly this exception for the signal
      // tables below, and `tenantMatches` already treats a global_temp row as authorised.
      const personaTenantFilter = { $in: [...new Set([...(tenantFilter.$in || [tenantFilter]), 'global_temp'])] };
      const isSebaLoad = tenantId === 'seba';
      const isContentProfileTenant = isLibraryAccountPageTenant(tenantId);

      const [
        peopleByAccountId,
        peopleByName,
        momentumByAccountId,
        momentumByName,
        allProcurementData,
        allDealsData,
        profileData,
        ispProfileData,
        contentProfileData,
        notesData,
        departmentsData
      ] = await Promise.all([
        PersonFinal.filter({ account_id: dealAccountIdFilter, tenant_id: personaTenantFilter }).catch(() => []),
        PersonFinal.filter({ account_name: accountData.account_name, tenant_id: personaTenantFilter }).catch(() => []),
        // Do not pin tenant_id on signal tables — V2 signals live in global_temp and
        // the backend scopes partitions from entitlement when tenant_id is unset.
        MomentumScore.filter({ account_id: accountData.account_id }).catch(() => []),
        MomentumScore.filter({ institution_name: accountData.account_name }).catch(() => []),
        Procurement.filter({ account_id: dealAccountIdFilter, tenant_id: tenantFilter }).catch(() => []),
        Deal.filter({ account_id: dealAccountIdFilter, tenant_id: tenantFilter }).catch(() => []),
        ProcurementProfileData.filter({ account_id: accountData.account_id, tenant_id: profileTenantFilter }).catch(() => []),
        isSebaLoad
          ? IspProfileData.filter({ account_id: accountData.account_id, tenant_id: 'seba' }).catch(() => [])
          : Promise.resolve([]),
        isContentProfileTenant
          ? ContentProfileData.filter({ account_id: accountData.account_id }).catch(() => [])
          : Promise.resolve([]),
        Note.filter({ account_id: accountData.account_id }).catch(() => []),
        isCalmedicaLoad ? Department.filter({ tenant_id: 'calmedica' }, "", 300).catch(() => []) : Promise.resolve([])
      ]);
      
      // Merge and dedupe personas
      // 1) Dedupe by id/person_id, preferring current tenant
      const allPeopleData = [...(peopleByAccountId || []), ...(peopleByName || [])];
      const uniquePeopleMap = new Map();
      allPeopleData.forEach(p => {
        const key = p.id || p.person_id;
        if (key) {
          const existing = uniquePeopleMap.get(key);
          if (!existing || (p.tenant_id === tenantId && existing.tenant_id !== tenantId)) {
            uniquePeopleMap.set(key, p);
          }
        }
      });
      // 2) Dedupe by name to avoid same-person-different-tenant duplicates (e.g. smowl + instructure)
      const normalizePersonName = (p) =>
        `${(p.first_name || '').trim()} ${(p.last_name || '').trim()}`.toLowerCase().replace(/\s+/g, ' ').trim();
      const byNameMap = new Map();
      [...uniquePeopleMap.values()].forEach(p => {
        const nameKey = normalizePersonName(p) || `__id_${p.id || p.person_id}`;
        const existing = byNameMap.get(nameKey);
        const pIsCurrentTenant = p.tenant_id === tenantId;
        const existingIsCurrentTenant = existing && existing.tenant_id === tenantId;
        if (!existing || (pIsCurrentTenant && !existingIsCurrentTenant)) {
          byNameMap.set(nameKey, p);
        }
      });
      uniquePeopleMap.clear();
      byNameMap.forEach((p) => {
        const key = p.id || p.person_id;
        if (key) uniquePeopleMap.set(key, p);
      });
      
      // Merge and dedupe momentum  
      const allMomentumData = [...(momentumByAccountId || []), ...(momentumByName || [])];
      const uniqueMomentumMap = new Map();
      allMomentumData.forEach(m => {
        if (m.id && !uniqueMomentumMap.has(m.id)) {
          uniqueMomentumMap.set(m.id, m);
        }
      });
      
      console.log('📊 Fetched data for account:', {
        accountId: accountData.account_id,
        accountName: accountData.account_name,
        peopleByAccountId: (peopleByAccountId || []).length,
        peopleByName: (peopleByName || []).length,
        uniquePeople: uniquePeopleMap.size,
        momentumByAccountId: (momentumByAccountId || []).length,
        momentumByName: (momentumByName || []).length,
        uniqueMomentum: uniqueMomentumMap.size
      });

      const tenantMatches = (record) => {
        if (!record) return false;
        // `global_temp` is the shared institutional layer. The backend only returns
        // these rows to a tenant entitled to them AND bounded to the accounts that
        // tenant has added, so a row arriving here is already authorised; re-filtering
        // it out only discards what was correctly served.
        if (record.tenant_id === 'global_temp') return true;
        if (tenantId === 'smowl') return record.tenant_id === 'smowl';
        if (record.tenant_id === tenantId || record.tenant_id === 'global') return true;
        if (Array.isArray(record.visible_to_tenants) && record.visible_to_tenants.includes(tenantId)) return true;
        if (record.tenant_overrides && record.tenant_overrides[tenantId]) return true;
        if (tenantId === 'instructure' && (record.tenant_id === 'default' || !record.tenant_id || record.tenant_id === 'clairelabs')) return true;
        if (tenantId === 'diambra') return record.tenant_id === 'diambra' || record.tenant_id === 'global';
        if (tenantId === 'edqwest') return record.tenant_id === 'edqwest' || record.tenant_id === 'global';
        if (tenantId === 'calmedica') return record.tenant_id === 'calmedica' || record.tenant_id === 'global';
        if (tenantId === 'plataformaa') return record.tenant_id === 'plataformaa';
        return false;
      };

      let finalAccount = applyTenantOverrides(accountData, tenantId);
      if (bounceRemovedFromList(finalAccount, navigate)) return;
      if (finalAccount.is_visible === false || !tenantMatches(finalAccount)) {
        console.warn(`Account '${finalAccount.account_name}' not accessible.`);
        if (!cached) {
          setAccount(null);
          setIsLoading(false);
        }
        return;
      }

      const filteredPeople = [...uniquePeopleMap.values()]
        .filter(p => {
          if (p.is_visible === false) return false;
          // The persona fetch above deliberately includes `global_temp`, and tenantMatches treats
          // any global_temp row as authorised — so honour `visible_to_tenants` where the build has
          // actually set it, or one tenant's people could surface on another tenant's page for the
          // same institution. Only an explicitly NON-EMPTY array gates: a great many existing rows
          // carry an empty or null array (146/146 on 4science once), and requiring it outright
          // would hide people who render correctly today.
          if (Array.isArray(p.visible_to_tenants) && p.visible_to_tenants.length > 0
              && !p.visible_to_tenants.includes(tenantId)) return false;
          return tenantMatches(p);
        })
        .map(p => {
          const withOverrides = applyTenantOverrides(p, tenantId);
          return withOverrides.is_visible !== false ? withOverrides : null;
        })
        .filter(p => p !== null);

      console.log('👥 Filtered personas:', filteredPeople.length);

      const filteredMomentum = [...uniqueMomentumMap.values()]
      .filter(m => tenantMatches(m))
      .map(m => applyTenantOverrides(m, tenantId))
      .map(m => ({ ...m, momentum_score_change: sanitizeMomentumChange(m.momentum_score_change) }))
      .sort((a, b) => {
        const dateA = new Date(a.date_published || a.weekly_period);
        const dateB = new Date(b.date_published || b.weekly_period);
        return dateB - dateA;
      });

    console.log('📊 Filtered momentum:', filteredMomentum.length);

        console.log('📊 Momentum matching debug:', {
          identifier,
          finalAccountName: finalAccount.account_name,
          totalMomentum: allMomentumData?.length,
          filteredCount: filteredMomentum.length,
          sampleMomentum: allMomentumData?.slice(0, 5).map(m => ({
            institution_name: m.institution_name,
            account_id: m.account_id,
            matchesByName: normalizeAccountName(m.institution_name) === normalizeAccountName(finalAccount.account_name)
          })),
          momentumWithMatchingName: allMomentumData?.filter(m => 
            normalizeAccountName(m.institution_name) === normalizeAccountName(finalAccount.account_name)
          ).length
        });

        const filteredProcurement = (allProcurementData || []).filter(p => {
        if (!matchesDealAccount(p)) return false;
        return tenantMatches(p);
      }).map(p => applyTenantOverrides(p, tenantId));

      const filteredDeals = (allDealsData || []).filter(d => {
        if (!matchesDealAccount(d)) return false;
        return tenantMatches(d);
      }).map(d => applyTenantOverrides(d, tenantId));

      let finalDepartments = [];
      if (isCalmedicaLoad && departmentsData && departmentsData.length > 0) {
        // Use real department data from departments table
        // Try matching by account_id first, then by account_name
        finalDepartments = departmentsData.filter(d => {
          const matchesById =
            d.account_id === accountData.account_id || d.account_id === identifier;
          const matchesByName =
            d.account_name &&
            normalizeAccountName(d.account_name) ===
              normalizeAccountName(accountData.account_name);
          return matchesById || matchesByName;
        });

        // Apply tenant overrides to departments like other entities
        finalDepartments = finalDepartments.map(d => applyTenantOverrides(d, tenantId));

        console.log('🏥 Loaded departments from Department entity:', {
          total: departmentsData.length,
          matchedForAccount: finalDepartments.length,
          accountId: accountData.account_id,
          accountName: accountData.account_name,
          sample: finalDepartments.slice(0, 2).map(d => ({
            department: d.department,
            score: d.department_fit_score,
            rationale: d.department_fit_rationale ? d.department_fit_rationale.substring(0, 50) + '...' : 'missing',
            deptAccountId: d.account_id,
            hasScore: !!d.department_fit_score,
            hasRationale: !!d.department_fit_rationale
          }))
        });
      }

      const normalizedDepartments = normalizeDepartments(finalDepartments, identifier);
      
      // Log normalized departments to verify data is preserved
      console.log('🏥 Normalized departments sample:', normalizedDepartments.slice(0, 2).map(d => ({
        department: d.department,
        score: d.department_fit_score,
        rationale: d.department_fit_rationale ? d.department_fit_rationale.substring(0, 50) + '...' : 'missing',
        hasScore: d.department_fit_score !== undefined && d.department_fit_score !== null,
        hasRationale: !!d.department_fit_rationale
      })));

      const filteredNotes = (notesData || []).filter(n => tenantMatches(n)).map(n => applyTenantOverrides(n, tenantId));
      // For learnwise: prefer learnwise-specific profile, fall back to global (matched by account_id)
      let finalProfileData = null;
      if (profileData && profileData.length > 0) {
        const learnwiseProfile = profileData.find(p => p.tenant_id === 'learnwise');
        const globalProfile = profileData.find(p => p.tenant_id === 'global');
        const preferred = learnwiseProfile || globalProfile || profileData[0];
        finalProfileData = applyTenantOverrides(preferred, tenantId);
      }

      const finalIspProfileData =
        ispProfileData && ispProfileData.length > 0 ? ispProfileData[0] : null;

      let finalContentProfileData = null;
      if (isContentProfileTenant && contentProfileData && contentProfileData.length > 0) {
        const preferred =
          contentProfileData.find((p) => p.tenant_id === tenantId) ||
          contentProfileData.find((p) => p.tenant_id === 'global_temp') ||
          contentProfileData.find((p) => p.tenant_id === 'global') ||
          contentProfileData[0];
        finalContentProfileData = preferred;
      }

      console.log('📊 AccountFinal: Mapping momentum scores to signals:', {
        momentumCount: filteredMomentum.length,
        accountId: accountData.account_id
      });

      const mappedSignals = (filteredMomentum || []).map((score, index) => {
        try {
          // Validate score data
          if (!score || !score.id) {
            console.warn(`⚠️ AccountFinal: Invalid momentum score at index ${index}:`, score);
            return null;
          }

          const scoreChange = score.signal_score ?? score.momentum_score_change ?? 0;
          const signal = {
            id: `momentum_${score.id}`,
            title: score.summary || `Momentum Update: ${score.institution_name || 'Unknown Institution'}`,
            description: translate('acctMomentumScoreChanged').split('{n}').join(String(scoreChange)),
            source_url: score.source_url || null,
            signal_type: score.signal_type || 'strategic_announcement',
            signal_date: score.date_published || score.weekly_period || new Date().toISOString(),
            tags: score.country ? [score.country] : [],
            impact_score: scoreChange,
            department: score.department || null
          };

          return signal;
        } catch (error) {
          console.error(`❌ AccountFinal: Error mapping momentum score at index ${index}:`, error, score);
          return null;
        }
      }).filter(signal => signal !== null); // Remove any null entries

      console.log('✅ AccountFinal: Mapped signals:', {
        totalSignals: mappedSignals.length,
        sampleSignal: mappedSignals[0]
      });

      setAccount(finalAccount);
      setPersonas(filteredPeople);
      setMomentumScores(filteredMomentum);
      setProcurements(filteredProcurement);
      setDeals(filteredDeals);
      setProcurementProfile(finalProfileData);
      setIspProfile(finalIspProfileData);
      setContentProfile(finalContentProfileData);
      setNotes(filteredNotes);
      
      console.log('📊 AccountFinal: Setting signals state:', {
        signalsCount: mappedSignals.length,
        accountId: finalAccount.account_id,
        accountName: finalAccount.account_name,
        signalsWithErrors: mappedSignals.filter(s => !s || !s.id).length
      });
      
      setSignals(mappedSignals);
      setDepartments(normalizedDepartments);
      setRateLimitError(false);
      setUsingStaleCache(false);
      
      // Use account_id for cache key to ensure consistency (UUID and text account_id resolve to same cache)
      const cacheKey = finalAccount.account_id || identifier;
      writeAccountCache(cacheKey, tenantId, {
        account: finalAccount,
        personas: filteredPeople,
        momentumScores: filteredMomentum,
        procurements: filteredProcurement,
        deals: filteredDeals,
        procurementProfile: finalProfileData,
        ispProfile: finalIspProfileData,
        contentProfile: finalContentProfileData,
        notes: filteredNotes,
        signals: mappedSignals,
        departments: normalizedDepartments
      });
      
      clearRateLimitCooldown();
      console.log('✅ AccountFinal: Fresh data loaded and cached');
      
    } catch (error) {
      console.error("Error loading account data:", error);

      if (isNotInPilotError(error)) {
        setPilotLocked(error.body || location.state?.pilotLocked || {});
        setAccount(null);
        setIsLoading(false);
        return;
      }
      
      const isRateLimit = error.response?.status === 429 || 
                         error.message?.includes('Rate limit') ||
                         error.message?.includes('429');
      
      if (isRateLimit) {
        console.warn('🚫 Rate limit hit');
        setRateLimitCooldown();
        setRateLimitError(true);
        
        if (!cached) {
          const fallbackCache = readAccountCache(identifier, tenantId);
          if (fallbackCache) {
            setAccount(fallbackCache.account);
            setPersonas(fallbackCache.personas || []);
            setSignals(fallbackCache.signals || []);
            setMomentumScores(fallbackCache.momentumScores || []);
            setProcurements(fallbackCache.procurements || []);
            setDeals(fallbackCache.deals || []);
            setProcurementProfile(fallbackCache.procurementProfile || null);
            setIspProfile(fallbackCache.ispProfile || null);
            setContentProfile(fallbackCache.contentProfile || null);
            setNotes(fallbackCache.notes || []);
            setDepartments(fallbackCache.departments || []);
            setUsingStaleCache(fallbackCache.isStale);
          } else {
            setAccount(null);
          }
        }
      } else if (!cached) {
        setAccount(null);
      }
    } finally {
      setIsLoading(false);
    }
  }, [currentTenant, contextUser, navigate, location.state]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const id = sanitizeId(params.get('id'));
    
    if (id) {
      loadAccountData(id);
    } else {
      setIsLoading(false);
      setAccount(null);
    }
  }, [location.search, loadAccountData]);

  const handleRetry = () => {
    const params = new URLSearchParams(location.search);
    const id = sanitizeId(params.get('id'));
    if (id) {
      clearRateLimitCooldown();
      setRateLimitError(false);
      setUsingStaleCache(false);
      loadAccountData(id, true);
    }
  };

  const adminTenant =
    contextUser?.role === 'admin'
      ? localStorage.getItem('admin-active-tenant')
      : null;
  const effectiveTenant = resolveEffectiveTenant(
    adminTenant,
    currentTenant,
    contextUser,
    account?.tenant_id || 'instructure'
  );

  useEffect(() => {
    let cancelled = false;
    if (!effectiveTenant) {
      setMarketModuleOn(false);
      setMarketLens(null);
      return undefined;
    }
    const cached = getCachedMarketModuleHint(effectiveTenant);
    if (cached) {
      setMarketModuleOn(cached.on);
      if (cached.lens) setMarketLens(cached.lens);
    }
    resolveMarketModuleHint(effectiveTenant)
      .then((hint) => {
        if (cancelled) return;
        setMarketModuleOn(hint.on);
        if (hint.lens) setMarketLens(hint.lens);
      })
      .catch(() => {
        if (!cancelled && !cached?.on) setMarketModuleOn(false);
      });
    return () => { cancelled = true; };
  }, [effectiveTenant]);

  useEffect(() => {
    const accountId = account?.account_id || account?.id;
    if (!effectiveTenant || !accountId) {
      setCompetitorProximityRow(null);
      return;
    }
    let cancelled = false;
    setCompetitorProximityRow(undefined);
    fetchAccountCompetitiveProximity(effectiveTenant, accountId, {})
      .then((data) => {
        if (!cancelled) setCompetitorProximityRow(data?.row ?? null);
      })
      .catch(() => {
        if (!cancelled) setCompetitorProximityRow(null);
      });
    return () => { cancelled = true; };
  }, [effectiveTenant, account?.account_id, account?.id]);

  // Prefetch Sales Map while user is on Overview so the tab is warm when opened.
  useEffect(() => {
    const accountId = account?.account_id || account?.id;
    if (!effectiveTenant || !accountId) return;
    if (effectiveTenant === 'seba' || account?.tenant_id === 'seba') return;
    prefetchAccountSalesMap(effectiveTenant, accountId, contextUser?.id);
  }, [effectiveTenant, account?.account_id, account?.id, account?.tenant_id, contextUser?.id]);

  const showPersonaEnrichCta = marketModuleOn !== false;

  const showCompetitorsTab = competitorProximityRow != null;
  const competitorTabCount = showCompetitorsTab ? (competitorProximityRow?.signal_count ?? 0) : 0;

  const handleSaveTargetProducts = useCallback(
    async (selectedValue) => {
      const recordId = account?.id || account?.account_id;
      if (!recordId || !contextUser) return;
      try {
        const tenantId = resolveEffectiveTenant(
          contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null,
          currentTenant,
          contextUser,
          account?.tenant_id || 'instructure'
        );
        const valueToSave =
          selectedValue == null || selectedValue === '' ? null : String(selectedValue).trim();
        const payload = { tenant_id: tenantId, target_products: valueToSave };
        const API_BASE_URL =
          window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
            ? 'https://api.neogtm.com'
            : 'https://api.neogtm.com';
        const headers = {
          'Content-Type': 'application/json',
          'x-tenant-id': tenantId,
          'x-user-id': contextUser?.id || '',
        };
        let res = await fetch(
          `${API_BASE_URL}/api/analytics-v2/accounts/${recordId}/target-products`,
          {
            method: 'PATCH',
            headers,
            credentials: 'include',
            body: JSON.stringify(payload),
          }
        );
        let updated;
        if (res.ok) {
          const data = await res.json();
          updated = data.account || data;
        } else if (res.status === 404) {
          const entityRes = await fetch(
            `${API_BASE_URL}/api/entities/AccountFinal/${encodeURIComponent(recordId)}`,
            {
              method: 'PATCH',
              headers,
              credentials: 'include',
              body: JSON.stringify({ target_products: valueToSave }),
            }
          );
          if (!entityRes.ok) {
            const errJson = await entityRes.json().catch(() => ({}));
            throw new Error(errJson.error || errJson.message || 'Save failed');
          }
          updated = await entityRes.json();
        } else {
          const errJson = await res.json().catch(() => ({}));
          throw new Error(errJson.error || errJson.message || 'Save failed');
        }
        const nextTp = updated.target_products ?? null;
        setAccount((prev) => (prev ? { ...prev, target_products: nextTp } : prev));
        const params = new URLSearchParams(location.search);
        const cacheId = sanitizeId(params.get('id'));
        if (cacheId) {
          const tid = resolveEffectiveTenant(
            contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null,
            currentTenant,
            contextUser,
            account?.tenant_id || 'instructure'
          );
          const cached = readAccountCache(cacheId, tid);
          if (cached) {
            writeAccountCache(cacheId, tid, {
              ...cached,
              account: { ...cached.account, target_products: nextTp },
            });
          }
        }
        notify({
          title: t('acctSaved'),
          description: t('acctTargetProductUpdated'),
          variant: 'success',
        });
      } catch (e) {
        console.error(e);
        notify({
          title: t('error'),
          description: e?.message || t('acctCouldNotSaveTargetProduct'),
          variant: 'destructive',
        });
        throw e;
      }
    },
    [account, contextUser, currentTenant, location.search, t]
  );

  const confirmRemoveFromList = useCallback(async () => {
    const workingId = account?.account_id || account?.id;
    if (!workingId || !effectiveTenant) return;
    setRemoveBusy(true);
    try {
      const result = await removeMarketAccount(
        effectiveTenant,
        { account_id: workingId },
        marketLens,
      );
      const interpreted = interpretRemoveAccountResult(result);
      if (!interpreted.ok) {
        notify({
          title: interpreted.message,
          variant: 'destructive',
        });
        return;
      }
      const params = new URLSearchParams(location.search);
      const cacheId = sanitizeId(params.get('id'));
      clearAccountPageCache(effectiveTenant, cacheId || account?.id);
      notify({
        title: t('acctRemovedFromListTitle', { name: account.account_name || t('acctAccountLower') }),
        description: t('acctRemovedFromListDesc'),
      });
      setRemoveOpen(false);
      navigate(createPageUrl('Accounts'));
    } catch (e) {
      notify({
        title: t('acctCouldNotRemoveAccount'),
        description: e?.message || t('acctPleaseTryAgainNoDot'),
        variant: 'destructive',
      });
    } finally {
      setRemoveBusy(false);
    }
  }, [account, effectiveTenant, marketLens, location.search, navigate, t]);

  const renewalRisk = useAccountRenewalRisk(effectiveTenant, marketLens, account);

  if (!contextUser) {
    return (
      <div className="p-8 space-y-6 bg-gray-50 min-h-screen">
        <Skeleton className="h-40 w-full" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-8 space-y-6 bg-gray-50 min-h-screen">
        <Skeleton className="h-40 w-full" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (pilotLocked) {
    return <AccountPilotLocked payload={pilotLocked} />;
  }

  if (!account) {
    return (
      <div className="text-center py-20">
        <Building2 className="w-12 h-12 mx-auto text-gray-300" />
        <h2 className="mt-4 text-xl font-semibold text-[#0A1A2F]">{t('accountNotFound')}</h2>
        <p className="text-[#5C6B7A]">{t('accountNotFoundDesc')}</p>
      </div>
    );
  }

  const isCalmedicaTenant = effectiveTenant === 'calmedica' || (account?.tenant_id === 'calmedica');
  const showDepartmentsTab = isCalmedicaTenant && departments.length > 0;
  const hasFinancialsTab = hasAccountFinancialsTab(account);
  const isSebaTenant = effectiveTenant === 'seba' || account?.tenant_id === 'seba';
  const showProcurementTab = !isSebaTenant;
  const showIspProfileTab = isSebaTenant;
  // Digital content offering for Demarque. Bibliotheca shares the library account page but not this tab.
  const showContentTab = isLibraryAccountPageTenant(effectiveTenant) && !isBibliothecaFamilyTenant(effectiveTenant);

  const allowedAccountTabs = isSebaTenant
    ? [
        'overview',
        ...(showIspProfileTab ? ['products'] : []),
        ...(hasFinancialsTab ? ['financials'] : []),
        'personas',
        'signals',
        ...(showCompetitorsTab ? ['competitors'] : []),
        'playbooks',
        'notes',
      ]
    : [
        'overview',
        ...(showContentTab ? ['content'] : []),
        ...(hasFinancialsTab ? ['financials'] : []),
        'sales-map',
        ...(showProcurementTab ? ['procurement'] : []),
        'personas',
        'signals',
        ...(showCompetitorsTab ? ['competitors'] : []),
        'playbooks',
        'notes',
      ];
  const defaultAccountTab =
    tabFromUrl === 'departments' && showDepartmentsTab
      ? 'departments'
      : allowedAccountTabs.includes(tabFromUrl)
        ? tabFromUrl
        : 'overview';
  
  const top3Departments = isCalmedicaTenant 
    ? [...departments].sort((a, b) => (Number(b.department_fit_score) || 0) - (Number(a.department_fit_score) || 0)).slice(0, 3)
    : [];

  /* ── Back goes where they CAME FROM, not always to Accounts ────────────────────────────────
   *
   * Same fix and same reasoning as PersonProfile (ab3b1d3), which has the long version of the
   * note. This page is reached from the Accounts list, but also from Market, from today's plan,
   * from a persona's account link, from search and from the connector's open_in_neo deep links —
   * and a hard `<Link to={createPageUrl('Accounts')}>` sent every one of those to a list the rep
   * may never have been on.
   *
   * `window.history.state.idx` is react-router's own position in the session history: > 0 means
   * there is an in-app entry behind this one, so navigate(-1) lands exactly where they were.
   * idx 0 means this page IS the first entry — a deep link, an emailed link, a refresh, a new
   * tab — where there is nothing to go back to and the Accounts list is the right destination.
   *
   * ⚠️ NOT the same as the navigate() after removing an account (~line 1025). That one must keep
   * going to the Accounts list: going "back" to the page of an account the rep just removed from
   * their list would show them a thing that is no longer there. */
  const hasHistoryBehind = typeof window !== 'undefined'
    && typeof window.history?.state?.idx === 'number'
    && window.history.state.idx > 0;

  const backLinkClass = 'inline-flex items-center gap-2 text-sm font-medium text-[#5C6B7A] hover:text-[#0A1A2F]';
  const backControl = hasHistoryBehind ? (
    <button type="button" onClick={() => navigate(-1)} className={backLinkClass}>
      <ChevronLeft className="w-4 h-4" />
      {t('back')}
    </button>
  ) : (
    /* Only label it "Back to Accounts" when that is genuinely where it goes. */
    <Link to={createPageUrl('Accounts')} className={backLinkClass}>
      <ChevronLeft className="w-4 h-4" />
      {t('backTo')} {t('accounts')}
    </Link>
  );

  console.log('🏥 Render check:', {
    currentTenant,
    accountTenantId: account?.tenant_id,
    isCalmedicaTenant,
    departmentsLength: departments.length,
    showDepartmentsTab,
    top3Departments: top3Departments.map(d => ({ dept: d.department, score: d.department_fit_score }))
  });

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 md:px-6 pt-4">
        {backControl}
      </div>
      
      <AccountHeader 
        account={account} 
        startChatSession={startChatSession} 
        currentTenant={currentTenant} 
        topDepartments={top3Departments || []}
        marketBadges={marketBadges}
        marketModuleOn={marketModuleOn}
        onRemoveFromList={() => setRemoveOpen(true)}
        renewalRisk={renewalRisk}
        onTierUpdate={async (newTier) => {
          setAccount(prev => ({ ...prev, tier: newTier, account_tier: newTier }));
          const params = new URLSearchParams(location.search);
          const id = sanitizeId(params.get('id'));
          if (id) {
            const tenantId = resolveEffectiveTenant(contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null, currentTenant, contextUser, account?.tenant_id || 'instructure');
            const cached = readAccountCache(id, tenantId);
            if (cached) {
              writeAccountCache(id, tenantId, {
                ...cached,
                account: { ...cached.account, tier: newTier, account_tier: newTier }
              });
            }
          }
        }}
        onTargetProductsUpdate={
          isSysExamProductContextTenant(effectiveTenant) ||
          effectiveTenant === 'learnwise' ||
          effectiveTenant === 'cayuse'
            ? handleSaveTargetProducts
            : undefined
        }
      />

      <CalmedicaOrganisationLinks
        accountId={account.account_id}
        accountName={account.account_name}
        tenantId={effectiveTenant}
      />
      
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 md:py-8">
        <div className="space-y-6">
          
          {rateLimitError && (
            <Alert className="border-yellow-200 bg-yellow-50">
              <AlertCircle className="h-4 w-4 text-yellow-600" />
              <AlertDescription className="text-yellow-800">
                <div className="flex items-center justify-between">
                  <span>{t('rateLimitReached')}</span>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={handleRetry}
                    className="ml-4"
                  >
                    <RefreshCw className="w-3 h-3 mr-1" />
                    {t('retry')}
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {usingStaleCache && !rateLimitError && (
            <Alert className="border-blue-200 bg-blue-50">
              <AlertCircle className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-800">
                <div className="flex items-center justify-between">
                  <span>{t('showingCachedData')}</span>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={handleRetry}
                    className="ml-4"
                  >
                    <RefreshCw className="w-3 h-3 mr-1" />
                    {t('refresh')}
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {marketTechUnavailable ? (
              <TechStack account={account} currentTenant={effectiveTenant} />
            ) : (
              <MarketAccountTechStack
                account={account}
                currentTenant={effectiveTenant}
                onUnavailable={() => setMarketTechUnavailable(true)}
                onBadges={setMarketBadges}
              />
            )}
            <LiveDealsCard procurements={procurements || []} deals={deals || []} accountId={account.account_id || account.id} />
            <DecisionWindow account={account} />
            <MomentumSummary
              momentumScores={momentumScores || []}
              momentum_12w={account.momentum_12w}
            />
          </div>

          <Card className="border-0 shadow-md">
            <CardContent className="p-0">
              <Tabs defaultValue={defaultAccountTab} key={defaultAccountTab}>
                <div className="overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                  <TabsList style={{ backgroundColor: '#ECFFFE' }}>
                    <TabsTrigger value="overview" className="text-xs md:text-sm px-2 md:px-3 py-1.5 data-[state=active]:bg-white data-[state=active]:text-[#0A1A2F] data-[state=inactive]:text-[#5C6B7A]">{t('overview')}</TabsTrigger>
                    {showContentTab && (
                      <TabsTrigger value="content" className="text-xs md:text-sm px-2 md:px-3 py-1.5 data-[state=active]:bg-white data-[state=active]:text-[#0A1A2F] data-[state=inactive]:text-[#5C6B7A]">
                        {t('acctTabContent')}
                      </TabsTrigger>
                    )}
                    {showIspProfileTab && (
                    <TabsTrigger value="products" className="text-xs md:text-sm px-2 py-1.5 data-[state=active]:bg-white data-[state=active]:text-[#0A1A2F] data-[state=inactive]:text-[#5C6B7A]">
                      {t('acctTabProducts')}
                    </TabsTrigger>
                    )}
                    {hasFinancialsTab && (
                      <TabsTrigger value="financials" className="text-xs md:text-sm px-2 md:px-3 py-1.5 data-[state=active]:bg-white data-[state=active]:text-[#0A1A2F] data-[state=inactive]:text-[#5C6B7A]">
                        {t('acctTabFinancialsAssets')}
                      </TabsTrigger>
                    )}
                    {!isSebaTenant && (
                      <TabsTrigger value="sales-map" className="text-xs md:text-sm px-2 md:px-3 py-1.5 data-[state=active]:bg-white data-[state=active]:text-[#0A1A2F] data-[state=inactive]:text-[#5C6B7A]">{t('acctTabSalesMap')}</TabsTrigger>
                    )}
                    {showDepartmentsTab && (
                      <TabsTrigger value="departments" className="text-xs md:text-sm px-2 md:px-3 py-1.5 data-[state=active]:bg-white data-[state=active]:text-[#0A1A2F] data-[state=inactive]:text-[#5C6B7A]">
                        {t('departments')} ({departments.length})
                      </TabsTrigger>
                    )}
                    {showProcurementTab && (
                    <TabsTrigger value="procurement" className="text-xs md:text-sm px-2 py-1.5 data-[state=active]:bg-white data-[state=active]:text-[#0A1A2F] data-[state=inactive]:text-[#5C6B7A]">
                      <span className="hidden sm:inline">{t('procurement')}</span>
                      <span className="sm:hidden">{t('procurement').substring(0, 4)}.</span>
                    </TabsTrigger>
                    )}
                    <TabsTrigger value="personas" className="text-xs md:text-sm px-2 md:px-3 py-1.5 data-[state=active]:bg-white data-[state=active]:text-[#0A1A2F] data-[state=inactive]:text-[#5C6B7A]">{t('personas')} ({personas.length})</TabsTrigger>
                    <TabsTrigger value="signals" className="text-xs md:text-sm px-2 md:px-3 py-1.5 data-[state=active]:bg-white data-[state=active]:text-[#0A1A2F] data-[state=inactive]:text-[#5C6B7A]">{t('signals')} ({signals.length})</TabsTrigger>
                    {showCompetitorsTab ? (
                      <TabsTrigger value="competitors" className="text-xs md:text-sm px-2 md:px-3 py-1.5 data-[state=active]:bg-white data-[state=active]:text-[#0A1A2F] data-[state=inactive]:text-[#5C6B7A]">
                        {t('acctTabCompetitorsCount', { n: competitorTabCount })}
                      </TabsTrigger>
                    ) : null}
                    <TabsTrigger value="playbooks" className="text-xs md:text-sm px-2 md:px-3 py-1.5 data-[state=active]:bg-white data-[state=active]:text-[#0A1A2F] data-[state=inactive]:text-[#5C6B7A]">{t('playbooks')} ({playbookCount})</TabsTrigger>
                    <TabsTrigger value="notes" className="text-xs md:text-sm px-2 md:px-3 py-1.5 data-[state=active]:bg-white data-[state=active]:text-[#0A1A2F] data-[state=inactive]:text-[#5C6B7A]">{t('notes')} ({notesCount})</TabsTrigger>
                  </TabsList>
                </div>
                <TabsContent value="overview" className="pt-4 md:pt-6 p-3 md:p-6">
                  <SellingRationaleSection sellingRationale={account.selling_rationale} />
                  <StrategyCards account={account} />
                </TabsContent>
                {showContentTab && (
                  <TabsContent value="content" className="pt-4 md:pt-6 p-3 md:p-6">
                    <ContentProfile
                      accountName={account.account_name}
                      accountId={account.account_id || account.id}
                      profileData={contentProfile}
                    />
                  </TabsContent>
                )}
                {hasFinancialsTab && (
                  <TabsContent value="financials" className="pt-4 md:pt-6 p-3 md:p-6">
                    <FinancialsTab account={account} financialData={account.financial_data} />
                  </TabsContent>
                )}
                {!isSebaTenant && (
                <TabsContent value="sales-map" className="pt-4 md:pt-6 p-0">
                  <SalesMapTab
                    accountId={account.account_id || account.id}
                    accountName={account.account_name}
                    tenantId={effectiveTenant}
                    startChatSession={startChatSession}
                  />
                </TabsContent>
                )}
                {showDepartmentsTab && (
                  <TabsContent value="departments" className="pt-4 md:pt-6 p-3 md:p-6">
                    <TooltipProvider>
                    <DepartmentsList departments={departments || []} />
                    </TooltipProvider>
                  </TabsContent>
                )}
                {showIspProfileTab && (
                <TabsContent value="products" className="pt-4 md:pt-6 p-3 md:p-6">
                  <IspProfile
                    accountName={account.account_name}
                    accountId={account.account_id || account.id}
                    profileData={ispProfile}
                  />
                </TabsContent>
                )}
                {showProcurementTab && (
                <TabsContent value="procurement" className="pt-4 md:pt-6 p-3 md:p-6">
                  <ProcurementProfile
                    accountName={account.account_name}
                    accountId={account.account_id || account.id}
                    globalPoolAccountId={account.global_pool_account_id}
                    procurements={procurements}
                    profileData={procurementProfile}
                  />
                </TabsContent>
                )}
                <TabsContent value="personas" className="pt-4 md:pt-6 p-3 md:p-6">
                  <div className="mb-4 flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-[#0A1A2F]">{t('keyContacts')}</h3>
                    <TooltipProvider delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button className="p-1 rounded-full hover:bg-gray-100 transition-colors">
                            <Info className="w-4 h-4 text-[#5C6B7A]" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent 
                          side="bottom"
                          align="start"
                          className="w-[90vw] max-w-[400px] md:w-[400px] bg-[#0A1A2F] text-white border-0 shadow-lg p-4"
                        >
                          <p className="text-sm leading-relaxed">
                            {t('personasOrganizedBy')}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <ArchetypeGrid
                    personas={personas || []}
                    accountId={account.account_id || account.id}
                    emptyCta={
                      showPersonaEnrichCta ? (
                        <div className="pt-1">
                          <Button
                            size="sm"
                            className="bg-[#00C2C2] hover:bg-[#00A8A8] text-white"
                            disabled={enrichBusy}
                            onClick={async () => {
                              let aid = account.account_id || account.id;
                              if (!aid || !effectiveTenant) return;
                              const s = String(aid);
                              if (effectiveTenant && s.startsWith(`${effectiveTenant}_`)) {
                                aid = s.slice(effectiveTenant.length + 1);
                              }
                              setEnrichBusy(true);
                              try {
                                let lens = marketLens;
                                if (!lens) {
                                  const hint = await resolveMarketModuleHint(effectiveTenant);
                                  if (!hint.on) {
                                    setMarketModuleOn(false);
                                    notify({
                                      title: t('acctEnrichmentUnavailable'),
                                      description: t('acctMarketEnrichmentNotEnabled'),
                                      variant: 'destructive',
                                    });
                                    return;
                                  }
                                  lens = hint.lens;
                                  setMarketModuleOn(true);
                                  if (lens) setMarketLens(lens);
                                }
                                if (!lens) {
                                  notify({
                                    title: t('acctCouldNotRequestEnrichment'),
                                    description: t('acctMarketLensNotConfigured'),
                                    variant: 'destructive',
                                  });
                                  return;
                                }
                                const res = await requestAccountEnrichment(
                                  effectiveTenant,
                                  aid,
                                  { scope: ['personas'] },
                                  lens,
                                );
                                const at = res?.requested_at || new Date().toISOString();
                                setEnrichQueuedAt(at);
                                notify({
                                  title: res?.already_open
                                    ? t('acctEnrichmentAlreadyRequested')
                                    : t('acctEnrichmentRequested'),
                                  description: ENRICH_REQUEST_TOAST,
                                });
                              } catch (e) {
                                if (e instanceof MarketModuleOffError) {
                                  setMarketModuleOn(false);
                                } else {
                                  notify({
                                    title: t('acctCouldNotRequestEnrichment'),
                                    description: e?.message || t('acctTryAgainLater'),
                                    variant: 'destructive',
                                  });
                                }
                              } finally {
                                setEnrichBusy(false);
                              }
                            }}
                          >
                            {enrichBusy
                              ? t('acctRequesting')
                              : enrichQueuedAt
                                ? t('acctEnrichmentRequested')
                                : t('acctRequestPersonaEnrichment')}
                          </Button>
                        </div>
                      ) : null
                    }
                  />
                </TabsContent>
                <TabsContent value="signals" className="pt-4 md:pt-6 p-3 md:p-6">
                  <AccountSignalList signals={signals || []} currentTenant={currentTenant} account={account} />
                </TabsContent>
                {showCompetitorsTab ? (
                  <TabsContent value="competitors" className="pt-4 md:pt-6 p-3 md:p-6">
                    <AccountCompetitorsTabContent
                      tenantId={effectiveTenant}
                      accountId={account.account_id || account.id}
                      personas={personas}
                    />
                  </TabsContent>
                ) : null}
                <TabsContent value="playbooks" className="pt-4 md:pt-6 p-3 md:p-6">
                  <PlaybooksTab
                    accountId={account.account_id || account.id}
                    personas={personas || []}
                    currentTenant={currentTenant}
                    onDataLoaded={(count) => setPlaybookCount(count)}
                  />
                </TabsContent>
                <TabsContent value="notes" className="pt-4 md:pt-6 p-3 md:p-6">
                  <Notes accountId={account.account_id || account.id} onNotesCountChange={(count) => setNotesCount(count)} />
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

        </div>
      </div>
      <RemoveAccountDialog
        open={removeOpen}
        accountName={account.account_name}
        busy={removeBusy}
        onOpenChange={setRemoveOpen}
        onConfirm={confirmRemoveFromList}
      />
    </div>
  );
}